import { useRef, useEffect, type MutableRefObject } from 'react';

import { useAtomValue } from 'jotai';
import { Camera, Vector3, Spherical } from 'three';
import { OrbitControls } from 'three-stdlib';

import { playback3dCameraSphericalAtom, playback3dCameraTargetAtom } from '@store';

type CameraTransition = {
    isTransitioning: boolean;
    progress: number;
    startPosition: Vector3;
    endPosition: Vector3;
    currentPosition: Vector3;
};

type UseCameraInitializationParams = {
    camera: Camera;
    controls: OrbitControls;
    controlTarget?: Vector3;
    panBoundsCenter?: Vector3;
    cameraTransitionRef: MutableRefObject<CameraTransition>;
    previousAnglesRef: MutableRefObject<{ azimuthal: number; polar: number }>;
};

// Restore the persisted camera snapshot once on mount.
// We intentionally do not re-sync continuously from atoms during interaction.
export function useCameraInitialization({
    camera,
    controls,
    controlTarget,
    panBoundsCenter,
    cameraTransitionRef,
    previousAnglesRef,
}: UseCameraInitializationParams) {
    const hasAppliedInitialCameraStateRef = useRef(false);
    const cameraSpherical = useAtomValue(playback3dCameraSphericalAtom);
    const cameraTarget = useAtomValue(playback3dCameraTargetAtom);

    useEffect(() => {
        if (hasAppliedInitialCameraStateRef.current || !cameraSpherical) return;

        const cameraTransition = cameraTransitionRef.current;

        let resolvedTarget: Vector3;

        if (controlTarget) {
            resolvedTarget = controlTarget.clone();
        } else if (cameraTarget) {
            resolvedTarget = new Vector3(cameraTarget.x, cameraTarget.y, cameraTarget.z);
        } else if (panBoundsCenter) {
            resolvedTarget = panBoundsCenter.clone();
        } else {
            resolvedTarget = controls.target.clone();
        }

        const offset = new Vector3().setFromSpherical(
            new Spherical(cameraSpherical.distance, cameraSpherical.polar, cameraSpherical.azimuthal),
        );

        cameraTransition.isTransitioning = false;
        cameraTransition.progress = 0;
        cameraTransition.startPosition.copy(resolvedTarget);
        cameraTransition.endPosition.copy(resolvedTarget);
        cameraTransition.currentPosition.copy(resolvedTarget);

        controls.target.copy(resolvedTarget);
        camera.position.copy(resolvedTarget.clone().add(offset));
        camera.lookAt(resolvedTarget);
        controls.update();

        previousAnglesRef.current = {
            azimuthal: controls.getAzimuthalAngle(),
            polar: controls.getPolarAngle(),
        };

        hasAppliedInitialCameraStateRef.current = true;
    }, [
        camera,
        controls,
        controlTarget,
        cameraSpherical,
        cameraTarget,
        panBoundsCenter,
        cameraTransitionRef,
        previousAnglesRef,
    ]);
}
